<script>
import DashboardLayout from '@/components/layouts/dashboard/DashboardLayout';
import UserForm from '@/components/users/UserForm';
import getErrorMessage from '@/modules/server-errors';

export default {
  name: 'NewUser',
  components: {
    DashboardLayout,
    UserForm
  },
  metaInfo() {
    return { title: this.title };
  },
  data() {
    return {
      title: this.$t('New User'),
      breadcrumbs: [{ text: this.$t('Users'), to: { name: 'Users' } }, { text: this.$t('New User') }],
      invalid: [],
      isDisabled: false,
      isFavorite: false,
      isSaving: false,
      permissions: [],
      user: {}
    };
  },
  mounted() {
    this.$refs.userForm.$refs.name.focus();
  },
  beforeRouteLeave(to, from, next) {
    // Check for unsaved changes
    if (this.$refs.userForm.isDirty()) {
      this.$confirm(this.$t("Your changes haven't been saved. Are you sure you want to continue?"), {
        title: this.$t('Unsaved Changes'),
        confirmButtonText: this.$t('Continue'),
        cancelButtonText: this.$t('Cancel')
      }).then(() => next());
    } else {
      next();
    }
  },
  methods: {
    // Cancels all changes and goes back to the users page
    handleCancel() {
      this.$router.push({ name: 'Users' });
    },

    // Saves the user
    handleSave() {
      const data = this.$refs.userForm.getData();
      this.isSaving = true;
      this.$http
        .post('/users', {
          name: data.name,
          email: data.email,
          avatar: data.avatar,
          bio: data.bio,
          isAdmin: data.isAdmin,
          canManageBilling: data.canManageBilling,
          canManageSettings: data.canManageSettings,
          canManageSites: data.canManageSites,
          canManageUsers: data.canManageUsers,
          permissions: data.permissions,
          message: {
            subject: this.$t('Welcome to {appName}', { appName: this.$store.state.metadata.appName }),
            intro: this.$t(
              'An account has been created for you to manage your website. Please follow the link below to confirm your account.'
            ),
            callToAction: this.$t('Confirm My Account'),
            outro: this.$t('We hope you enjoy using {appName}', { appName: this.$store.state.metadata.appName })
          }
        })
        .then(res => {
          this.invalid = [];
          this.user = res.data.user;
          this.$refs.userForm.resetCleanState();

          // Set permissions
          return this.$http.put(`/users/${this.user.id}/permissions`, {
            permissions: data.permissions
          });
        })
        .then(() => this.$router.push({ name: 'User', params: { userId: this.user.id } }))
        .catch(err => {
          const data = err.response.data;
          this.invalid = Array.isArray(data.invalid) ? data.invalid : [];
          this.$notify({
            type: 'error',
            title: this.$t('Error'),
            message: getErrorMessage(err.response.data.code)
          });
        })
        .finally(() => (this.isSaving = false));
    }
  }
};
</script>

<template>
  <dashboard-layout size="small">
    <!-- Breadcrumbs -->
    <template slot="breadcrumbs">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item v-for="(item, index) in breadcrumbs" :key="index" :to="item.to">
          {{ item.text }}
        </el-breadcrumb-item>
      </el-breadcrumb>
    </template>

    <!-- User form -->
    <user-form
      ref="userForm"
      :disabled="isDisabled"
      :invalid="invalid"
      :saving="isSaving"
      is-new
      @cancel="handleCancel"
      @submit="handleSave"
    />
  </dashboard-layout>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';
</style>
